/**
 * Typed error surface for API_CONTRACT_SV_v0.1 §2 (capture plane).
 *
 * The contract's error envelope is `{code, message, details?}` with typed codes per endpoint.
 * `SvApiError` carries the HTTP status, the machine code (or `UNKNOWN` when the server sent no
 * envelope) and the untrusted `details` block — callers branch on `code`, never on prose.
 */

/** Every capture-plane code named in §2, plus the console-plane codes a shared server may echo. */
export type SvErrorCode =
  | 'SV_LINK_EXPIRED'
  | 'SV_CASE_NOT_CAPTURABLE'
  | 'SV_CONSENT_MANDATORY_DECLINED'
  | 'SV_CHAIN_MISMATCH'
  /** The be refused to seal a CLIP under 10 240 bytes (US-03482 G5) — the step is recorded again. */
  | 'SV_CLIP_EMPTY'
  | 'SV_SHOT_SEALED'
  | 'SV_CHUNKS_MISSING'
  | 'SV_MANDATORY_SHOT_MISSING'
  | 'SV_CHALLENGE_UNANSWERED'
  | 'SV_VALIDATION'
  | 'SV_ILLEGAL_TRANSITION'
  // SV-5-2: the live leg (DEC-089 ruling 8)
  | 'SV_LIVE_NOT_CLAIMED'
  | 'SV_LIVE_ENDED'
  | 'SV_LIVE_NOT_REQUESTED'
  | 'SV_LIVE_RAIL_UNAVAILABLE'
  // SV-5-2 second round: the live-leg re-consent gate + the instruction prompt channel
  | 'SV_CONSENT_LIVE_REQUIRED'
  | 'SV_LIVE_PROMPT_CLOSED'
  // SPEC-15 / S15-1: the OTP front door on the link (contract v0.1.9 §C). The contract names ONLY
  // SV_OTP_CHANNEL_UNAVAILABLE; the wrong-code / locked / expired codes below are the client's expectation
  // of the be's naming and are matched TOLERANTLY (see otpFailureOf in flow/OtpStep.tsx) until the be's
  // build/wire/sv-exchange-otp.json pins them.
  | 'SV_OTP_REQUIRED'
  | 'SV_OTP_INVALID'
  | 'SV_OTP_EXPIRED'
  | 'SV_OTP_LOCKED'
  | 'SV_LINK_LOCKED'
  | 'SV_OTP_CHANNEL_UNAVAILABLE'
  // the per-case token buckets (stamp tile / preview, the help request — contract v0.1.13 §11a)
  | 'SV_RATE_LIMITED'
  // US-03482 §14f: the ID-document photo is masked at receipt. The 422s are answers about the photo (take a NEW one,
  // never resend these bytes); the 503 is a transient (the redactor could not run — resend after `Retry-After`).
  | 'SV_DOCUMENT_NOT_RECOGNISED'
  | 'SV_IMAGE_TOO_LARGE'
  | 'SV_IMAGE_UNDECODABLE'
  | 'SV_DOCUMENT_NOT_ONE_CHUNK'
  | 'SV_REDACTION_UNAVAILABLE'
  | 'UNAUTHORIZED'
  | 'UNKNOWN';

/**
 * The be prefixes every envelope message with its code (`SV_RATE_LIMITED: your earlier request …`) — the code is for
 * branching, the words are for people. When a surface shows the server's own words, it shows them without the prefix.
 */
export function serverWords(message: string): string {
  return message.replace(/^[A-Z][A-Z0-9_]*:\s*/, '');
}

export interface SvErrorBody {
  readonly code: string;
  readonly message: string;
  readonly details?: unknown;
}

export class SvApiError extends Error {
  readonly name = 'SvApiError';
  constructor(
    public readonly status: number,
    public readonly code: SvErrorCode,
    message: string,
    /** The server's `details` block — untrusted input; pick out only what you need. */
    public readonly details: unknown = null,
    /** The server's code string VERBATIM (null when it sent none) — for tolerant matching of codes the contract
     *  has not yet named; branch on `code` first, on this only as a fallback. Never rendered. */
    public readonly serverCode: string | null = null,
    /** The response's `Retry-After` in seconds, when the server sent one the page may read (null otherwise). */
    public readonly retryAfterS: number | null = null,
  ) {
    super(message);
  }
}

/** A transport-level failure (no HTTP answer at all) — offline, DNS, aborted. */
export class SvNetworkError extends Error {
  readonly name = 'SvNetworkError';
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
  }
}

const KNOWN_CODES: ReadonlySet<string> = new Set<SvErrorCode>([
  'SV_LINK_EXPIRED',
  'SV_CASE_NOT_CAPTURABLE',
  'SV_CONSENT_MANDATORY_DECLINED',
  'SV_CHAIN_MISMATCH',
  'SV_CLIP_EMPTY',
  'SV_SHOT_SEALED',
  'SV_CHUNKS_MISSING',
  'SV_MANDATORY_SHOT_MISSING',
  'SV_CHALLENGE_UNANSWERED',
  'SV_VALIDATION',
  'SV_ILLEGAL_TRANSITION',
  'SV_LIVE_NOT_CLAIMED',
  'SV_LIVE_ENDED',
  'SV_LIVE_NOT_REQUESTED',
  'SV_LIVE_RAIL_UNAVAILABLE',
  'SV_CONSENT_LIVE_REQUIRED',
  'SV_LIVE_PROMPT_CLOSED',
  'SV_OTP_REQUIRED',
  'SV_OTP_INVALID',
  'SV_OTP_EXPIRED',
  'SV_OTP_LOCKED',
  'SV_LINK_LOCKED',
  'SV_OTP_CHANNEL_UNAVAILABLE',
  'SV_RATE_LIMITED',
  'SV_DOCUMENT_NOT_RECOGNISED',
  'SV_IMAGE_TOO_LARGE',
  'SV_IMAGE_UNDECODABLE',
  'SV_DOCUMENT_NOT_ONE_CHUNK',
  'SV_REDACTION_UNAVAILABLE',
  'UNAUTHORIZED',
]);

/**
 * Build the typed error from a status + parsed body. Tolerates the be's problem+json shape
 * (`{title, detail, status}`) so a server fault before the SV envelope lands is still a sentence
 * a human can read — but never the raw JSON body (the 2026-08-25 droom lesson).
 */
export function toSvApiError(status: number, body: unknown, fallback: string, retryAfterS: number | null = null): SvApiError {
  let code: SvErrorCode = status === 401 || status === 403 ? 'UNAUTHORIZED' : 'UNKNOWN';
  let message = fallback;
  let details: unknown = null;
  let serverCode: string | null = null;
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.code === 'string' && b.code) serverCode = b.code;
    if (typeof b.code === 'string' && KNOWN_CODES.has(b.code)) code = b.code as SvErrorCode;
    const msg =
      (typeof b.message === 'string' && b.message) ||
      (typeof b.detail === 'string' && b.detail) ||
      (typeof b.title === 'string' && b.title) ||
      null;
    if (msg) message = msg;
    if ('details' in b) details = b.details;
    // SV-5-2: 409 SV_LIVE_NOT_CLAIMED carries `retryAfterS` at the top level of the envelope — the wait cadence.
    else if (typeof b.retryAfterS === 'number') details = { retryAfterS: b.retryAfterS };
  }
  return new SvApiError(status, code, message, details, serverCode, retryAfterS);
}

export function isSvError(e: unknown, code: SvErrorCode): e is SvApiError {
  return e instanceof SvApiError && e.code === code;
}
